/**
 * Auditor evaluation fields for control-based internal audit (My Audits).
 * Stored values are stable English snake_case keys; labels come from i18n.
 */

export const MEASURE_IMPLEMENTATION_STATUS_VALUES = [
  "implemented",
  "mostly_implemented",
  "partially_implemented",
  "compensatory_control_implemented",
  "not_applicable",
];

export const MEASURE_EFFECTIVENESS_STATUS_VALUES = [
  "effective",
  "inactive",
  "partially_active",
];

export const CRITICALITY_LEVEL_VALUES = [
  "very_high",
  "high",
  "medium",
  "low",
];

export const AUDIT_METHOD_VALUES = [
  "interview",
  "review",
  "walkthrough",
  "observation",
  "examination_of_records",
  "analytical_procedures",
  "reperformance",
  "sampling",
  "log_review",
  "configuration_review",
  "physical_inspection",
  "external_confirmation",
  "safety_audit",
  "penetration_test",
  "source_code_analysis",
  "other",
];

/** Keys persisted for control auditor response (plus legacy strips on save). */
export const CONTROL_AUDITOR_RESPONSE_KEYS = [
  "measure_implementation_status",
  "measure_effectiveness_status",
  "criticality_level",
  "audit_methods",
  "comment",
  "evidences",
  "notes",
];

/**
 * @param {Record<string, unknown>|null|undefined} answer
 * @returns {boolean}
 */
export function hasAnyAuditorDropdownField(answer) {
  if (!answer || typeof answer !== "object") return false;
  if (answer.measure_implementation_status) return true;
  if (answer.measure_effectiveness_status) return true;
  if (answer.criticality_level) return true;
  if (Array.isArray(answer.audit_methods) && answer.audit_methods.length > 0) return true;
  return false;
}

/**
 * All required auditor dropdowns filled (three selects + at least one audit method).
 * @param {Record<string, unknown>|null|undefined} answer
 * @returns {boolean}
 */
export function isControlAuditorEvaluationComplete(answer) {
  if (!answer || typeof answer !== "object") return false;
  const impl = typeof answer.measure_implementation_status === "string" && answer.measure_implementation_status;
  const eff = typeof answer.measure_effectiveness_status === "string" && answer.measure_effectiveness_status;
  const crit = typeof answer.criticality_level === "string" && answer.criticality_level;
  const methods = Array.isArray(answer.audit_methods) ? answer.audit_methods : [];
  return Boolean(impl && eff && crit && methods.length > 0);
}

/**
 * Sidebar status dot: not_started | in_progress | completed
 * @param {Record<string, unknown>|null|undefined} answer
 * @returns {"not_started"|"in_progress"|"completed"}
 */
export function getAuditorControlSidebarStatus(answer) {
  if (isControlAuditorEvaluationComplete(answer)) return "completed";
  if (hasAnyAuditorDropdownField(answer)) return "in_progress";
  return "not_started";
}

/**
 * Strip deprecated maturity/objective keys; keep only allowed auditor response keys.
 * @param {Record<string, Record<string, unknown>>} controlAnswers
 * @returns {Record<string, Record<string, unknown>>}
 */
export function normalizeControlAnswersForSave(controlAnswers) {
  const out = {};
  for (const [controlId, raw] of Object.entries(controlAnswers || {})) {
    if (!raw || typeof raw !== "object") continue;
    const next = {};
    for (const key of CONTROL_AUDITOR_RESPONSE_KEYS) {
      if (raw[key] === undefined) continue;
      if (key === "audit_methods") {
        next[key] = Array.isArray(raw[key]) ? [...raw[key]] : [];
        continue;
      }
      next[key] = raw[key];
    }
    out[controlId] = next;
  }
  return out;
}
